<script lang="ts" setup>
import { ref, onMounted, watch, type Component } from 'vue';
import ContentWithLoadingSkeleton from '@/components/ContentLoadingSkeleton/ContentWithLoadingSkeleton.vue';
import { fetchFolders, fetchFolderArticles, searchArticles } from '@/helpers/api/knowledgeBase/freshdeskKnowledgeBase';
import type { Folder, Article } from '@/helpers/api/knowledgeBase/types';
import {
  PhWrench,
  PhCpu,
  PhQuestion,
  PhFileText,
  PhChatCircleText,
  PhBookOpen,
  PhMagnifyingGlass,
  PhCaretDown,
  PhFile,
  PhArrowRight,
} from '@phosphor-icons/vue';

const folders = ref<Folder[]>([]);
const folderArticles = ref<Record<number, Article[]>>({});
const isLoadingArticles = ref<Record<number, boolean>>({});
const isLoading = ref(false);
const searchTerm = ref('');
const searchResults = ref<Article[]>([]);
const hasSearched = ref(false);
const folderExpanded = ref<Record<number, boolean>>({});

const FOLDER_ICONS: Record<string, Component> = {
  'Media Player Troubleshooting': PhWrench,
  'Media Player Hardware': PhCpu,
  'Media Player FAQ': PhQuestion,
  'Documents': PhFileText,
  'Digital Signage Portal (DSP) FAQ': PhChatCircleText,
};

function getFolderIcon(name: string): Component {
  return FOLDER_ICONS[name] ?? PhBookOpen;
}

function toggleFolder(folderId: number) {
  folderExpanded.value[folderId] = !folderExpanded.value[folderId];
}

async function loadFolders() {
  isLoading.value = true;
  try {
    folders.value = await fetchFolders();
    console.log('Folders loaded:', folders.value);
    console.log('Number of folders:', folders.value.length);

    folders.value.forEach(folder => {
      console.log(`Folder ${folder.id}: ${folder.name} has ${folder.articles_count} articles`);
      folderExpanded.value[folder.id] = true;
      if (folder.articles_count > 0) {
        loadFolderArticles(folder.id);
      }
    });
  } catch (error) {
    console.error('Failed to load folders:', error);
  } finally {
    isLoading.value = false;
    console.log('isLoading set to false');
  }
}

async function loadFolderArticles(folderId: number) {
  isLoadingArticles.value[folderId] = true;
  console.log(`Loading articles for folder ${folderId}...`);
  try {
    const articles = await fetchFolderArticles(folderId);
    console.log(`Folder ${folderId} loaded ${articles.length} articles:`, articles);
    folderArticles.value[folderId] = articles;
    console.log('folderArticles state:', folderArticles.value);
  } catch (error) {
    console.error(`Failed to load articles for folder ${folderId}:`, error);
  } finally {
    isLoadingArticles.value[folderId] = false;
    console.log(`Finished loading articles for folder ${folderId}`);
  }
}

async function handleSearch() {
  if (!searchTerm.value.trim()) {
    searchResults.value = [];
    hasSearched.value = false;
    return;
  }

  isLoading.value = true;
  hasSearched.value = true;
  try {
    searchResults.value = await searchArticles(searchTerm.value);
  } catch (error) {
    console.error('Failed to search articles:', error);
    searchResults.value = [];
  } finally {
    isLoading.value = false;
  }
}

watch(searchTerm, (newValue) => {
  if (!newValue.trim()) {
    searchResults.value = [];
    hasSearched.value = false;
  }
});

onMounted(() => {
  loadFolders();
});
</script>

<template>
  <div class="bui-dashboard-page kb-page">
    <!-- Page header -->
    <div class="kb-header">
      <h1 class="kb-title">Knowledge Base</h1>
      <div class="kb-title-accent"></div>
    </div>

    <!-- Search card -->
    <div class="kb-search-card">
      <div class="kb-search-inner">
        <PhMagnifyingGlass class="kb-search-icon" weight="regular" :size="20" />
        <input
          v-model="searchTerm"
          type="text"
          placeholder="Enter your search term here..."
          class="kb-search-input"
          data-testid="kb-search"
          @keyup.enter="handleSearch"
        />
        <button class="kb-search-btn" data-testid="kb-search-submit" @click="handleSearch">SEARCH</button>
      </div>
    </div>

    <!-- Main content -->
    <ContentWithLoadingSkeleton :isLoading="isLoading">

      <!-- Search results -->
      <div v-if="searchResults.length > 0" class="kb-results">
        <h2 class="kb-section-title">Search Results</h2>
        <div class="kb-flat-list">
          <div v-for="article in searchResults" :key="article.id" class="kb-result-item">
            <RouterLink :to="`/knowledge-base/article/${article.id}`" class="kb-article__link">
              <PhFile class="kb-article__file-icon" weight="regular" :size="16" />
              <span class="kb-article__title">{{ article.title }}</span>
              <PhArrowRight class="kb-article__arrow" weight="bold" :size="14" />
            </RouterLink>
            <div v-if="article.folder_name" class="kb-result-item__folder">
              {{ article.folder_name }}
            </div>
          </div>
        </div>
      </div>

      <!-- No results -->
      <div v-else-if="hasSearched && searchResults.length === 0" class="kb-empty">
        <h2 class="kb-section-title">No Results Found</h2>
        <p class="kb-empty__message">
          No articles found matching "{{ searchTerm }}". Try a different search term.
        </p>
      </div>

      <!-- Folder cards -->
      <div v-else class="kb-folders">
        <div v-for="folder in folders" :key="folder.id" class="kb-card">
          <!-- Card header — toggles expand/collapse -->
          <button
            class="kb-card__header"
            @click="toggleFolder(folder.id)"
            :aria-expanded="folderExpanded[folder.id]"
            :aria-controls="`kb-articles-${folder.id}`"
          >
            <div class="kb-card__icon-wrap">
              <component
                :is="getFolderIcon(folder.name)"
                class="kb-card__icon"
                weight="light"
                :size="28"
              />
            </div>
            <div class="kb-card__title-area">
              <span class="kb-card__title">{{ folder.name }}</span>
              <span class="kb-card__count">({{ folder.articles_count }})</span>
            </div>
            <PhCaretDown
              class="kb-card__caret"
              :class="{ 'kb-card__caret--open': folderExpanded[folder.id] }"
              weight="bold"
              :size="18"
            />
          </button>

          <!-- Articles grid -->
          <div
            v-show="folderExpanded[folder.id]"
            :id="`kb-articles-${folder.id}`"
            class="kb-card__articles"
          >
            <div v-if="isLoadingArticles[folder.id]" class="kb-card__loading">
              Loading articles...
            </div>
            <template v-else-if="folderArticles[folder.id]">
              <RouterLink
                v-for="article in folderArticles[folder.id]"
                :key="article.id"
                :to="`/knowledge-base/article/${article.id}`"
                class="kb-article__link"
              >
                <PhFile class="kb-article__file-icon" weight="regular" :size="15" />
                <span class="kb-article__title">{{ article.title }}</span>
                <PhArrowRight class="kb-article__arrow" weight="bold" :size="13" />
              </RouterLink>
            </template>
          </div>
        </div>
      </div>

    </ContentWithLoadingSkeleton>
  </div>
</template>

<style lang="scss" scoped>
// ─── Page canvas ─────────────────────────────────────────────────────────────

.kb-page {
  background-color: var(--bui-page-fill, #ff007b);
  flex: 1 1 auto;
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  justify-content: flex-start;
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px);

  @include screen-width-down(768px) {
    padding: 24px 20px;
  }
}

// ─── Header ──────────────────────────────────────────────────────────────────

.kb-header {
  margin-bottom: var(--bui-page-section-gap, 24px);
}

.kb-title {
  margin: 0 0 10px;
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  font-weight: 700;
  font-size: clamp(28px, 3.5vw, 42px);
  color: var(--bui-page-head, #fffcfd);
  letter-spacing: 0.01em;
}

.kb-title-accent {
  width: 64px;
  height: 3px;
  border-radius: 2px;
  background: color-mix(in srgb, var(--bui-page-head, #fffcfd) 50%, transparent);
}

// ─── Search card ─────────────────────────────────────────────────────────────

.kb-search-card {
  background: var(--dash-card-fill, rgba(75, 24, 134, 0.45));
  border: 1px solid var(--dash-card-border, rgba(255, 178, 219, 0.15));
  border-radius: var(--dash-card-radius, 16px);
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 32px rgba(75, 24, 134, 0.16));
  padding: 20px 24px;
  margin-bottom: var(--bui-page-section-gap, 24px);
}

.kb-search-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 860px;
  position: relative;
}

.kb-search-icon {
  position: absolute;
  left: 14px;
  color: var(--bui-ter-input-hint, #ffb2db);
  pointer-events: none;
  flex-shrink: 0;
}

.kb-search-input {
  flex: 1;
  padding: 10px 14px 10px 42px;
  background: var(--bui-ter-input-fill, rgba(42, 41, 105, 0.12));
  border: 1px solid var(--bui-ter-input-border, rgba(42, 41, 105, 0.04));
  border-radius: 8px;
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  font-size: 14px;
  color: var(--bui-ter-input-text, #fffcfd);
  min-width: 0;
  transition: border-color 0.15s ease;

  &::placeholder {
    color: var(--bui-ter-input-hint, #ffb2db);
  }

  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--bui-page-head, #fffcfd) 40%, transparent);
  }
}

.kb-search-btn {
  padding: 10px 24px;
  background: var(--bui-cta-pri-fill, #baff5c);
  color: var(--bui-cta-pri-text, #007366);
  border: none;
  border-radius: 8px;
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.88;
  }

  &:focus-visible {
    outline: 2px solid var(--bui-page-head, #fffcfd);
    outline-offset: 2px;
  }
}

// ─── Section titles ───────────────────────────────────────────────────────────

.kb-section-title {
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  font-weight: 700;
  font-size: 20px;
  color: var(--bui-page-head, #fffcfd);
  margin: 0 0 16px;
}

// ─── Article link (shared by search results + folder cards) ───────────────────

.kb-article__link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--kb-link-color, #ffd9e6);
  text-decoration: none;
  font-size: 14px;
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  padding: 8px 10px;
  border-radius: 6px;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: var(--kb-link-hover, #fffcfd);
    background: color-mix(in srgb, var(--bui-page-head, #fffcfd) 8%, transparent);
  }

  &:focus-visible {
    outline: 2px solid var(--bui-page-head, #fffcfd);
    outline-offset: 1px;
  }
}

.kb-article__file-icon {
  flex-shrink: 0;
  opacity: 0.7;
}

.kb-article__title {
  flex: 1;
  line-height: 1.4;
}

.kb-article__arrow {
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s ease;

  .kb-article__link:hover & {
    opacity: 0.7;
  }
}

// ─── Search results ───────────────────────────────────────────────────────────

.kb-results {
  background: var(--dash-card-fill, rgba(75, 24, 134, 0.45));
  border: 1px solid var(--dash-card-border, rgba(255, 178, 219, 0.15));
  border-radius: var(--dash-card-radius, 16px);
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 32px rgba(75, 24, 134, 0.16));
  padding: 24px;
}

.kb-flat-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.kb-result-item {
  display: flex;
  flex-direction: column;
}

.kb-result-item__folder {
  font-size: 12px;
  color: var(--dash-card-desc, rgba(255, 252, 253, 0.72));
  margin-left: 33px;
  margin-bottom: 4px;
}

// ─── Empty state ──────────────────────────────────────────────────────────────

.kb-empty {
  text-align: center;
  padding: 3rem 1rem;
}

.kb-empty__message {
  font-size: 15px;
  color: var(--dash-card-desc, rgba(255, 252, 253, 0.72));
  line-height: 1.6;
  margin: 0;
}

// ─── Folder cards ─────────────────────────────────────────────────────────────

.kb-folders {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
}

.kb-card {
  background: var(--dash-card-fill, rgba(75, 24, 134, 0.45));
  border: 1px solid var(--dash-card-border, rgba(255, 178, 219, 0.15));
  border-radius: var(--dash-card-radius, 16px);
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 32px rgba(75, 24, 134, 0.16));
  overflow: hidden;
}

// ─── Card header (toggle button) ─────────────────────────────────────────────

.kb-card__header {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 18px 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  color: inherit;
  transition: background 0.15s ease;

  &:hover {
    background: color-mix(in srgb, var(--bui-page-head, #fffcfd) 6%, transparent);
  }

  &:focus-visible {
    outline: 2px solid var(--bui-page-head, #fffcfd);
    outline-offset: -2px;
    border-radius: var(--dash-card-radius, 16px);
  }
}

.kb-card__icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--dash-icon-bg, rgba(164, 32, 200, 0.45));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dash-icon-fg, #fffcfd);
  flex-shrink: 0;
}

.kb-card__icon {
  display: block;
}

.kb-card__title-area {
  flex: 1;
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.kb-card__title {
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  font-weight: 700;
  font-size: 17px;
  color: var(--dash-card-text, #fffcfd);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kb-card__count {
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  font-size: 13px;
  font-weight: 400;
  color: var(--dash-card-desc, rgba(255, 252, 253, 0.72));
  flex-shrink: 0;
}

.kb-card__caret {
  flex-shrink: 0;
  color: var(--dash-card-desc, rgba(255, 252, 253, 0.72));
  transform: rotate(0deg);
  transition: transform 0.22s ease;

  &--open {
    transform: rotate(180deg);
  }
}

// ─── Articles grid ────────────────────────────────────────────────────────────

.kb-card__articles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 2px 8px;
  padding: 4px 16px 16px;
  border-top: 1px solid color-mix(in srgb, var(--dash-card-border, rgba(255, 178, 219, 0.15)) 80%, transparent);

  @include medium-devices-down {
    grid-template-columns: 1fr;
  }
}

.kb-card__loading {
  font-size: 13px;
  color: var(--dash-card-desc, rgba(255, 252, 253, 0.72));
  font-style: italic;
  padding: 8px 10px;
  grid-column: 1 / -1;
}
</style>
